import React, { useState, useEffect } from 'react';
import { Play, Star, Lock, Check, ChevronRight, Shield, Heart, Ear, Scale, Sword, Target, MessageCircle, Send, Award } from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { apiClient } from '../api/client';

const CATEGORY_COLORS = {
  social: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', gradient: 'from-blue-500 to-cyan-500' },
  confidence: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', gradient: 'from-amber-500 to-orange-500' },
  assertiveness: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', gradient: 'from-purple-500 to-violet-500' },
  conflict_resolution: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', gradient: 'from-yellow-500 to-amber-500' },
};

const DIFFICULTY_STYLE = {
  easy: 'bg-green-100 text-green-700',
  medium: 'bg-amber-100 text-amber-700',
  hard: 'bg-red-100 text-red-700',
};

const RolePlayScenarios = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeScenario, setActiveScenario] = useState(null);
  const [responses, setResponses] = useState(['']);
  const [selfScore, setSelfScore] = useState(3);
  const [completing, setCompleting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    apiClient.request('/api/self-dev/role-plays')
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleStart = (scenario) => {
    setActiveScenario(scenario);
    setResponses(['']);
    setSelfScore(3);
    setResult(null);
  };

  const handleComplete = async () => {
    const filtered = responses.filter(r => r.trim());
    if (filtered.length === 0) return;
    setCompleting(true);
    try {
      const res = await apiClient.request(`/api/self-dev/role-plays/${activeScenario.scenario_id}/complete`, {
        method: 'POST',
        body: JSON.stringify({ responses: filtered, self_score: selfScore }),
      });
      setResult(res);
    } catch {}
    setCompleting(false);
  };

  if (loading) return <DashboardLayout><div className="p-8 text-center text-gray-400">Loading scenarios...</div></DashboardLayout>;
  if (!data) return <DashboardLayout><div className="p-8 text-center text-gray-400">Could not load scenarios</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-8 max-w-4xl mx-auto" data-testid="role-play-page">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl sm:text-3xl font-black text-gray-900 flex items-center justify-center gap-2">
            <Target size={28} className="text-red-500" />
            Role-Play Scenarios
          </h1>
          <p className="text-sm text-gray-500 mt-1">Practice real-world situations in a safe space</p>
          <div className="flex items-center justify-center gap-4 mt-3 text-xs">
            <span className="font-bold text-indigo-600">{data.total_completed}/{data.total_scenarios} Completed</span>
            {data.total_completed > 0 && (
              <>
                <span className="text-gray-300">|</span>
                <span className="font-bold text-amber-600">Avg Score: {data.average_score}</span>
              </>
            )}
          </div>
        </div>

        {/* Active Scenario / Play Mode */}
        {activeScenario && !result && (
          <div className="mb-6 bg-white rounded-2xl border-2 border-indigo-200 shadow-lg overflow-hidden" data-testid="active-scenario">
            <div className={`p-5 sm:p-6 bg-gradient-to-r ${CATEGORY_COLORS[activeScenario.category]?.gradient || 'from-gray-500 to-gray-600'} text-white`}>
              <p className="text-xs uppercase tracking-wider opacity-75 mb-1">{activeScenario.category}</p>
              <h2 className="text-xl font-black">{activeScenario.title}</h2>
              <p className="text-sm opacity-80 mt-1">{activeScenario.description}</p>
            </div>

            <div className="p-5 sm:p-6">
              {/* AvA's opening */}
              <div className="flex gap-3 mb-4">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                  <MessageCircle size={14} className="text-indigo-600" />
                </div>
                <div className="bg-indigo-50 rounded-2xl rounded-tl-sm p-3 flex-1">
                  <p className="text-xs font-bold text-indigo-600 mb-0.5">{activeScenario.ava_role}</p>
                  <p className="text-sm text-gray-800">{activeScenario.opening_line}</p>
                </div>
              </div>

              {/* Objectives */}
              <div className="mb-4 p-3 bg-gray-50 rounded-xl">
                <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-2">Objectives</p>
                {activeScenario.objectives.map((obj, i) => (
                  <p key={i} className="text-xs text-gray-600 flex items-center gap-1.5 mb-1">
                    <Target size={10} className="text-indigo-500 flex-shrink-0" /> {obj}
                  </p>
                ))}
              </div>

              {/* Tips */}
              <div className="mb-4 p-3 bg-amber-50 rounded-xl border border-amber-200">
                <p className="text-[10px] uppercase tracking-wider font-bold text-amber-600 mb-2">Tips from AvA</p>
                {activeScenario.tips.map((tip, i) => (
                  <p key={i} className="text-xs text-amber-800 mb-1">• {tip}</p>
                ))}
              </div>

              {/* Response inputs */}
              <div className="mb-4">
                <p className="text-xs font-medium text-gray-700 mb-2">Your responses (what would you say?):</p>
                {responses.map((r, i) => (
                  <div key={i} className="flex gap-2 mb-2">
                    <div className="flex-1">
                      <textarea
                        value={r}
                        onChange={e => {
                          const nr = [...responses];
                          nr[i] = e.target.value;
                          setResponses(nr);
                        }}
                        placeholder={`Response ${i + 1}...`}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none"
                        rows={2}
                        data-testid={`response-input-${i}`}
                      />
                    </div>
                  </div>
                ))}
                {responses.length < 4 && (
                  <button
                    onClick={() => setResponses([...responses, ''])}
                    className="text-xs text-indigo-600 font-medium hover:text-indigo-700"
                    data-testid="add-response-btn"
                  >
                    + Add another response
                  </button>
                )}
              </div>

              {/* Self-assessment */}
              <div className="mb-4">
                <p className="text-xs font-medium text-gray-700 mb-2">How well do you think you handled it? (1-5)</p>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map(s => (
                    <button
                      key={s}
                      onClick={() => setSelfScore(s)}
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                        s <= selfScore ? 'bg-amber-400 text-white scale-105' : 'bg-gray-100 text-gray-400'
                      }`}
                      data-testid={`self-score-${s}`}
                    >
                      <Star size={16} fill={s <= selfScore ? 'white' : 'none'} />
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-2">
                <button onClick={() => setActiveScenario(null)} className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-xl text-sm font-medium">Cancel</button>
                <button
                  onClick={handleComplete}
                  disabled={completing || responses.filter(r => r.trim()).length === 0}
                  className="flex-1 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl text-sm disabled:opacity-50"
                  data-testid="complete-roleplay-btn"
                >
                  {completing ? 'Scoring...' : 'Submit & Score'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="mb-6 bg-white rounded-2xl border-2 border-green-200 shadow-lg p-6 text-center" data-testid="roleplay-result">
            <Award size={40} className="text-amber-500 mx-auto mb-3" />
            <h3 className="text-2xl font-black text-gray-900 mb-1">Score: {result.score}/100</h3>
            {result.is_new_best && <p className="text-xs font-bold text-green-600 mb-2">New Personal Best!</p>}
            <p className="text-sm text-gray-600 mb-4 max-w-md mx-auto">{result.feedback}</p>
            <p className="text-xs text-amber-600 font-semibold mb-4">+{result.hearts_earned} hearts earned</p>
            <div className="flex gap-2 max-w-sm mx-auto">
              <button onClick={() => { setResult(null); setActiveScenario(null); }} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-xl text-sm">Back to List</button>
              <button onClick={() => { setResult(null); setResponses(['']); setSelfScore(3); }} className="flex-1 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold rounded-xl text-sm" data-testid="retry-roleplay-btn">Try Again</button>
            </div>
          </div>
        )}

        {/* Scenario Cards */}
        {!activeScenario && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="scenario-grid">
            {data.scenarios.map(s => {
              const cat = CATEGORY_COLORS[s.category] || CATEGORY_COLORS.social;
              return (
                <div
                  key={s.scenario_id}
                  className={`${cat.bg} ${cat.border} border rounded-2xl overflow-hidden transition-all hover:shadow-md`}
                  data-testid={`scenario-${s.scenario_id}`}
                >
                  <div className="p-4 sm:p-5">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-1.5">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${cat.text} ${cat.bg}`}>{s.category}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${DIFFICULTY_STYLE[s.difficulty]}`}>{s.difficulty}</span>
                      </div>
                      {s.completed && (
                        <div className="flex items-center gap-1 text-xs font-bold text-green-600">
                          <Check size={12} /> {s.best_score}pts
                        </div>
                      )}
                    </div>
                    <h3 className="text-base font-bold text-gray-900 mb-1">{s.title}</h3>
                    <p className="text-xs text-gray-600 mb-3 line-clamp-2">{s.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-gray-400 flex items-center gap-1">
                        <Heart size={10} className="text-pink-400" /> +{s.hearts_reward} hearts
                      </span>
                      <button
                        onClick={() => handleStart(s)}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                          s.completed
                            ? 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                            : `bg-gradient-to-r ${cat.gradient} text-white hover:shadow-md`
                        }`}
                        data-testid={`start-${s.scenario_id}`}
                      >
                        <Play size={14} />
                        {s.completed ? 'Replay' : 'Start'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default RolePlayScenarios;
